import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import PageLayout from './shared/PageLayout';
import FormCard from './shared/FormCard';
import Input from './shared/Input';
import PasswordInput from './shared/PasswordInput';
import Button from './shared/Button';
import ErrorMessage from './shared/ErrorMessage';
import { ROUTES } from '../../constants';

interface PasswordRequirements {
  minLength: boolean;
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}

const ForgotPasswordScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [touched, setTouched] = useState({
    email: false,
    code: false,
    password: false,
    confirmPassword: false,
  });
  const [step, setStep] = useState<'email' | 'reset'>('email');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = useMemo(() => {
    if (!email) return true; // Don't show error if empty
    return emailRegex.test(email);
  }, [email]);

  // Password requirements validation
  const passwordRequirements: PasswordRequirements = useMemo(() => {
    return {
      minLength: newPassword.length >= 8,
      hasUpperCase: /[A-Z]/.test(newPassword),
      hasLowerCase: /[a-z]/.test(newPassword),
      hasNumber: /[0-9]/.test(newPassword),
      hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword),
    };
  }, [newPassword]);

  const isPasswordValid = useMemo(() => {
    return Object.values(passwordRequirements).every((req) => req === true);
  }, [passwordRequirements]);

  const passwordsMatch = useMemo(() => {
    if (!confirmPassword) return true; // Don't show error if empty
    return newPassword === confirmPassword;
  }, [newPassword, confirmPassword]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValidEmail || !email.trim()) {
      setTouched({ ...touched, email: true });
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const cleanEmail = email.trim().toLowerCase();

      // Send forgot password code via AWS Amplify
      await Auth.forgotPassword(cleanEmail);

      // Move to reset step
      setStep('reset');
    } catch (err: unknown) {
      setIsLoading(false);
      if (typeof err === 'object' && err && 'code' in err) {
        const errorObj = err as { code?: string; message?: string };
        switch (errorObj.code) {
          case 'UserNotFoundException':
            setError('No account found with this email address.');
            break;
          case 'LimitExceededException':
            setError('Too many attempts. Please try again later.');
            break;
          default:
            setError(
              errorObj.message || 'Something went wrong. Please try again.',
            );
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code.trim() || !isPasswordValid || !passwordsMatch) {
      setTouched({
        ...touched,
        code: true,
        password: true,
        confirmPassword: true,
      });
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const cleanEmail = email.trim().toLowerCase();

      // Submit password reset with code
      await Auth.forgotPasswordSubmit(cleanEmail, code.trim(), newPassword);

      // Success - navigate to login
      navigate(ROUTES.LOGIN, {
        state: { email: cleanEmail, passwordReset: true },
      });
    } catch (err: unknown) {
      setIsLoading(false);
      if (typeof err === 'object' && err && 'code' in err) {
        const errorObj = err as { code?: string; message?: string };
        switch (errorObj.code) {
          case 'CodeMismatchException':
            setError('Invalid verification code. Please check and try again.');
            break;
          case 'ExpiredCodeException':
            setError(
              'Verification code has expired. Please request a new one.',
            );
            break;
          case 'InvalidPasswordException':
            setError('Password does not meet requirements.');
            break;
          default:
            setError(
              errorObj.message || 'Something went wrong. Please try again.',
            );
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (step === 'email') {
        handleEmailSubmit(e as unknown as React.FormEvent);
      } else {
        handlePasswordReset(e as unknown as React.FormEvent);
      }
    }
  };

  const handleBackToLogin = () => {
    navigate(ROUTES.LOGIN);
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setError('');

    try {
      const cleanEmail = email.trim().toLowerCase();
      await Auth.forgotPassword(cleanEmail);
      setError('');
      // Show success message or toast
    } catch (err: unknown) {
      if (typeof err === 'object' && err && 'code' in err) {
        const errorObj = err as { code?: string; message?: string };
        setError(
          errorObj.message || 'Failed to resend code. Please try again.',
        );
      } else {
        setError('Failed to resend code. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Render password reset form
  if (step === 'reset') {
    const isFormValid =
      code.trim() &&
      isPasswordValid &&
      newPassword.trim() &&
      passwordsMatch &&
      confirmPassword.trim();

    return (
      <PageLayout>
        <FormCard
          title="Reset password"
          subtitle={
            <>
              Enter the verification code sent to <strong>{email}</strong> and
              your new password
            </>
          }
          className="border-1 border-[#f5f8f9] space-y-[16px] mt-[calc(-24px-0px)] tabletSm:mt-[calc(-24px-0px)] tabletMd:mt-[calc(-32px-0px)] desktopSm:mt-[calc(-40px-0px)]"
        >
          <form onSubmit={handlePasswordReset}>
            <Input
              id="reset-code"
              type="text"
              label="Verification code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onBlur={() => setTouched({ ...touched, code: true })}
              onKeyPress={handleKeyPress}
              placeholder="Enter verification code"
              error={
                touched.code && !code.trim()
                  ? 'Please enter the verification code'
                  : undefined
              }
              touched={touched.code}
            />

            <PasswordInput
              id="new-password"
              label="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onBlur={() => setTouched({ ...touched, password: true })}
              onKeyPress={handleKeyPress}
              placeholder="Enter your password"
              error={
                touched.password && !isPasswordValid
                  ? 'Password does not meet requirements'
                  : undefined
              }
              touched={touched.password}
              showRequirements={touched.password && !!newPassword}
              passwordRequirements={passwordRequirements}
            />

            <Input
              id="confirm-password"
              type="password"
              label="Enter password again"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => setTouched({ ...touched, confirmPassword: true })}
              onKeyPress={handleKeyPress}
              placeholder="Confirm your password"
              error={
                touched.confirmPassword && !passwordsMatch
                  ? 'Passwords do not match'
                  : undefined
              }
              touched={touched.confirmPassword}
            />

            <ErrorMessage message={error} />

            <Button type="submit" disabled={!isFormValid || isLoading}>
              {isLoading ? 'Resetting password...' : 'Reset password'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isLoading}
              className="text-sm text-blue-600 hover:text-blue-800 transition-colors disabled:opacity-50"
            >
              Resend code
            </button>
          </div>

          <Button variant="link" onClick={handleBackToLogin} className="mt-4">
            ← Back to login
          </Button>
        </FormCard>
      </PageLayout>
    );
  }

  // Render email entry form
  return (
    <PageLayout>
      <FormCard
        title="Forgot password"
        subtitle="Enter your email address and we'll send you a verification code to reset your password."
        className="border-1 border-[#f5f8f9] space-y-[16px] mt-[calc(-24px-0px)] tabletSm:mt-[calc(-24px-0px)] tabletMd:mt-[calc(-32px-0px)] desktopSm:mt-[calc(-40px-0px)]"
      >
        <form onSubmit={handleEmailSubmit}>
          <Input
            id="forgot-email"
            type="email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched({ ...touched, email: true })}
            onKeyPress={handleKeyPress}
            placeholder="Enter your email"
            error={
              touched.email && !isValidEmail
                ? 'Please enter a valid email address'
                : undefined
            }
            touched={touched.email}
          />

          <ErrorMessage message={error} />

          <Button
            type="submit"
            disabled={!email.trim() || !isValidEmail || isLoading}
          >
            {isLoading ? 'Sending...' : 'Send verification code'}
          </Button>
        </form>

        <Button variant="link" onClick={handleBackToLogin} className="mt-4">
          ← Back to login
        </Button>
      </FormCard>
    </PageLayout>
  );
};

export default ForgotPasswordScreen;
